import moment from "moment";
import { ApiVehicle } from "Types/api/Vehicle";

export const dataMiner = (formData: any) => {
    const data = formData;
    return function getData(index: number, key: string) {
        const layer = data[index];
        if (!layer) {
            return "";
        }

        const value = layer[key];
        return value || "";
    };
};

export const convertJsonToFormData = (form: any) => {
    const formData = new FormData();
    Object.keys(form).forEach((key) => {
        formData.append(key, form[key]);
    });
    return formData;
};

const dealInfo = (vehicle: ApiVehicle) => {
    return {
        purchase_loan:
            vehicle.payments?.selected === "loan" || vehicle.payments?.selected === "lease"
                ? vehicle.payments?.[vehicle.payments?.selected || "cash"]?.offers?.[
                      vehicle.payments?.[vehicle.payments?.selected || "cash"]?.selected_offer ?? 0
                  ].lender
                : undefined,
        purchase_make: vehicle.make,
        purchase_model: vehicle.model,
        purchase_price: vehicle.payments?.[vehicle.payments?.selected || "cash"]?.price,
        purchase_type: vehicle.payments?.selected,
        purchase_year: vehicle.year,
    };
};

export const splitDate = (theDate: string) => {
    const d = moment(theDate).utc();
    return {
        day: d.format("D"),
        month: d.format("M"),
        year: d.format("YYYY"),
    };
};

const rentOrOwn = (own: string) => {
    return own === "yes" ? "Y" : "N";
};

const buildDateOfBirth = (birthday: string, prefix = "primary") => {
    const separatedBirthday = splitDate(birthday);
    const output = {};
    output[`${prefix}_birthdate_month`] = separatedBirthday.month;
    output[`${prefix}_birthdate_day`] = separatedBirthday.day;
    output[`${prefix}_birthdate_year`] = separatedBirthday.year;
    return output;
};

const removeDashes = (str: string) => {
    return str.replace(/[-]/g, "");
};

const tradeInInfo = (tradeIn: any) => {
    if (!tradeIn.make) {
        return {
            purchase_tradein: "N",
        };
    }

    return {
        purchase_tradein: "Y",
        purchase_tradein_year: tradeIn.year,
        // tslint:disable-next-line: object-literal-sort-keys
        purchase_tradein_make: tradeIn.make,
        purchase_tradein_model: tradeIn.model,
        purchase_tradein_bank: "",
        purchase_tradein_balance: tradeIn.balance,
        purchase_tradein_mileage: tradeIn.odometerAmount,
    };
};

const addressInfo = (getValue: any, prefix = "primary", index: number) => {
    const output = {};
    output[`${prefix}_address_line1`] = getValue(index, "address1");
    output[`${prefix}_address_line2`] = getValue(index, "address2");
    output[`${prefix}_address_city`] = getValue(index, "city");
    output[`${prefix}_address_state`] = getValue(index, "state");
    output[`${prefix}_address_zip`] = getValue(index, "zipCode");
    output[`${prefix}_address_own`] = rentOrOwn(getValue(index, "owner"));
    output[`${prefix}_address_payment`] = rentOrOwn(getValue(index, "monthlyPayment"));

    const movedIn = splitDate(getValue(index, "movingDate"));
    output[`${prefix}_address_since_month`] = movedIn.month;
    output[`${prefix}_address_since_year`] = movedIn.year;
    return output;
};

const greaterThanZero = (amount: string): boolean =>
    !amount || (!Number.isNaN(parseFloat(amount)) && parseFloat(amount) > 0);

const workHistoryInfo = (getValue: any, prefix = "primary", index: number) => {
    const output = {};
    output[`${prefix}_work_name`] = getValue(index, `currentEmployerName`);
    output[`${prefix}_work_title`] = getValue(index, `jobTitle`);
    output[`${prefix}_work_phone`] = getValue(index, `primaryWorkPhone`);
    const splitStartDate = splitDate(getValue(index, `startDate`));
    output[`${prefix}_work_since_month`] = splitStartDate.month;
    output[`${prefix}_work_since_year`] = splitStartDate.year;
    output[`${prefix}_work_previous_name`] = getValue(index, `previousEmployerName`);
    output[`${prefix}_work_previous_title`] = getValue(index, "");
    output[`${prefix}_work_address`] = getValue(index, `workAddress`);
    output[`${prefix}_work_address_city`] = getValue(index, `workCity`);
    output[`${prefix}_work_address_state`] = getValue(index, `workState`);
    output[`${prefix}_work_zip`] = getValue(index, `workPostal`);
    output[`${prefix}_income_work`] = getValue(index + 1, `primaryIncome`);
    output[`${prefix}_income_additional_source`] = getValue(index + 1, "additionalSource");

    const additionalIncome = getValue(index + 1, "additionalIncome");
    if (greaterThanZero(additionalIncome)) {
        output[`${prefix}_income_additional`] = additionalIncome;
    }

    return output;
};

export const mapFormDataToCreditApp = (formData: any, dealData: any, tradeIn: any, dealerId: string) => {
    const getValue = dataMiner(formData);
    const primary = {
        primary_first_name: getValue(0, "firstName"),
        primary_middle_initial: getValue(0, "middleInitial"),
        // tslint:disable-next-line: object-literal-sort-keys
        primary_last_name: getValue(0, "lastName"),
        primary_email: getValue(0, "emailAddress"),
        primary_phone_day: getValue(0, "primaryPhone"),
        primary_phone_evening: getValue(0, "secondaryPhone"),
        primary_ssn: removeDashes(getValue(1, "ssn")),
        primary_drivers_license: getValue(1, "driversLicense"),
        primary_drivers_license_state: getValue(1, "driversLicenseState"),
        primary_marital_status: getValue(1, "maritalStatus"),
        ...buildDateOfBirth(getValue(1, "birthDay"), "primary"),
        ...addressInfo(getValue, "primary", 2),
        ...workHistoryInfo(getValue, "primary", 3),
    } as any;

    const hasCosigner = getValue(5, "hasCosigner");

    const cosigner = hasCosigner
        ? ({
              joint_application: "joint",
              joint_relationship: getValue(5, "jointRelationship"),
              secondary_first_name: getValue(6, "firstName"),
              secondary_middle_initial: getValue(6, "middleInitial"),
              // tslint:disable-next-line: object-literal-sort-keys
              secondary_last_name: getValue(6, "lastName"),
              secondary_email: getValue(6, "emailAddress"),
              secondary_phone_day: getValue(6, "primaryPhone"),
              secondary_phone_evening: getValue(6, "secondaryPhone"),
              ...buildDateOfBirth(getValue(7, "birthDay"), "secondary"),
              secondary_ssn: removeDashes(getValue(7, "ssn")),
              secondary_drivers_license: getValue(7, "driversLicense"),
              secondary_drivers_license_state: getValue(7, "driversLicenseState"),
              ...addressInfo(getValue, "secondary", 8),
              ...workHistoryInfo(getValue, "secondary", 9),
          } as any)
        : { joint_application: "individual" };

    const creditApp = { ...primary, ...cosigner, ...dealData, ...tradeIn };

    creditApp.quiet = "true";
    creditApp.partial = "false";
    creditApp.dealer_id = dealerId;
    creditApp.source = "DealerInspire - Online Shopper Electric";
    creditApp.accept_terms = true;

    const acceptPrivacy = getValue(11, "acceptPrivacy");
    if (acceptPrivacy) creditApp.accept_privacy = true;

    return creditApp;
};

export const submitCreditApp = async (personalData: any, vehicle: ApiVehicle, tradePending: any, dealerId: string) => {
    const vehicleData = dealInfo(vehicle);
    const tradeInData = tradeInInfo(tradePending);
    const requestBody = convertJsonToFormData(mapFormDataToCreditApp(personalData, vehicleData, tradeInData, dealerId));

    return fetch("https://api.prod.secure.dealerinspire.com/application", {
        body: requestBody,
        headers: {
            accept: "application/json",
        }, // cannot use the same OnlineShopper's headers as it throws the error "The requested route does not exist".
        method: "POST",
        mode: "cors",
    });
};
